import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import './RowActionsMenu.css';

const GAP = 4;
const ESTIMATED_MENU_HEIGHT = 96; // ~2 rows; first pass only, before the menu can be measured

/**
 * Three-dot row action menu that renders its dropdown in a portal at the
 * document root, positioned fixed against the trigger button (PRD-1119).
 *
 * Rendering inside a table/scroll container clipped the menu (Edit/Delete
 * hidden until the user scrolled). A portal + fixed positioning escapes the
 * clip. The menu renders once hidden, is measured, then placed: right-aligned
 * to the trigger, flipped above when it doesn't fit below, and kept `edge`
 * inside the viewport on every side (PRD-1606). Escape closes it and hands
 * focus back to the trigger.
 *
 * children is a render prop receiving a `close` callback:
 *   <RowActionsMenu>{(close) => (<button onClick={() => { close(); ... }}>…)}</RowActionsMenu>
 *
 * Optional, for surfaces built to the Bridge Icon trigger (Library, PRD-1606);
 * every default keeps the original behaviour:
 *   triggerClassName  extra class on the trigger (size and states)
 *   iconSize          the three-dot glyph's size (Icon's default otherwise)
 *   iconWeight        the glyph's weight, for a documented exception to Icon's
 *                     size rule (Library's bold trigger, PRD-1630)
 *   gap / edge        space between trigger and menu, and the least space kept
 *                     from every viewport edge
 *   ariaDescribedBy   id of text that describes the trigger (an archived item's state)
 *   triggerTip        the trigger's ResUI tooltip (data-tip)
 *   disclosure        the menu leaves focus on its trigger, so it is a disclosure:
 *                     aria-expanded, plus aria-controls while open; no
 *                     aria-haspopup or role=menu
 */
function RowActionsMenu({
  children,
  ariaLabel = 'Actions',
  menuClassName = '',
  triggerClassName = '',
  iconSize,
  iconWeight,
  gap = GAP,
  edge = GAP,
  disclosure = false,
  ariaDescribedBy,
  triggerTip,
}) {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // `close({ restoreFocus: true })` hands focus back to the trigger, for an option that opens a
  // modal: the modal then returns focus there when it closes (PRD-1621).
  const close = useCallback((opts) => {
    setOpen(false);
    if (opts?.restoreFocus) btnRef.current?.focus();
  }, []);

  // Two passes: until the menu has rendered there is nothing to measure, so the
  // first pass places it hidden on an estimate and the second measures it.
  const position = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menu = menuRef.current;
    const height = menu ? menu.offsetHeight : ESTIMATED_MENU_HEIGHT;
    const width = menu ? menu.offsetWidth : 0;
    let top = rect.bottom + gap;
    if (top + height > window.innerHeight - edge) {
      const above = rect.top - gap - height;
      top = above >= edge ? above : Math.max(edge, window.innerHeight - edge - height);
    }
    const left = Math.max(edge, Math.min(rect.right - width, window.innerWidth - edge - width));
    setStyle({ position: 'fixed', top, left, ...(menu ? {} : { visibility: 'hidden' }) });
  }, [gap, edge]);

  useLayoutEffect(() => {
    if (open) position();
  }, [open, position]);

  useLayoutEffect(() => {
    if (open && style?.visibility === 'hidden') position();
  }, [open, style, position]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        close();
      }
    };
    // While this menu is open it OWNS Escape, and says so by stopping the event.
    //
    // Hosts listen for Escape on `document` too (see useEscapeKey), and this
    // menu is mounted inside them — so without this, one keypress closes the
    // menu AND the host. In the document review modal that meant Escape
    // discarded every staged resolution on the way out.
    //
    // Capture phase is what makes it work: `document` is the first hop of the
    // capture path and the last of the bubble path, so a capture listener here
    // runs before any host's bubble listener, and stopping propagation there
    // prevents the rest of the path — including document's own bubble phase.
    // Registration order can't be relied on instead: this listener is added
    // when the menu OPENS, which is always after the host mounted its own.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
      if (btnRef.current) btnRef.current.focus();
    };
    // Any layout shift invalidates the fixed position — just close.
    const onShift = () => close();
    // Focus leaving both the trigger and the menu (Shift+Tab, or the page moving
    // focus) closes it too, so it never floats detached from the user.
    const onFocusIn = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        close();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onShift);
    window.addEventListener('scroll', onShift, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onShift);
      window.removeEventListener('scroll', onShift, true);
    };
  }, [open, close]);

  // The menu is portalled to the end of <body>, so the natural Tab order runs
  // from the trigger to the next control on the page and skips the menu. Tab
  // from an open trigger steps into the menu instead; Tab past its last item
  // (or Shift+Tab before its first) closes it and returns to the trigger.
  const menuButtons = () => Array.from(menuRef.current?.querySelectorAll('button:not([disabled])') || []);

  // A role=menu also moves by ArrowDown / ArrowUp, wrapping at either end (Shared Queries
  // handoff 2, Context Menu); ArrowDown from an open trigger steps into it like Tab does.
  const arrows = !disclosure;

  const onTriggerKeyDown = (e) => {
    const stepIn = (e.key === 'Tab' && !e.shiftKey) || (arrows && e.key === 'ArrowDown');
    if (!stepIn || !open) return;
    const [first] = menuButtons();
    if (!first) return;
    e.preventDefault();
    first.focus();
  };

  const onMenuKeyDown = (e) => {
    if (arrows && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      const items = menuButtons();
      if (!items.length) return;
      e.preventDefault();
      const i = items.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
      items[next].focus();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = menuButtons();
    const leaving = e.shiftKey ? document.activeElement === items[0] : document.activeElement === items[items.length - 1];
    if (!leaving) return;
    e.preventDefault();
    close();
    if (btnRef.current) btnRef.current.focus();
  };

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        className={`row-actions-button ${triggerClassName}`.trim()}
        aria-label={ariaLabel}
        aria-haspopup={disclosure ? undefined : 'menu'}
        aria-expanded={open}
        aria-controls={disclosure && open ? menuId : undefined}
        aria-describedby={ariaDescribedBy}
        data-tip={triggerTip}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onKeyDown={onTriggerKeyDown}
      >
        <Icon name="more_vert" size={iconSize} weight={iconWeight} />
      </button>
      {open && style &&
        createPortal(
          <div
            ref={menuRef}
            id={disclosure ? menuId : undefined}
            className={`row-actions-menu ${menuClassName}`}
            style={style}
            role={disclosure ? undefined : 'menu'}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={onMenuKeyDown}
          >
            {typeof children === 'function' ? children(close) : children}
          </div>,
          document.body,
        )}
    </>
  );
}

export default RowActionsMenu;
